// src/feed/feedDefaults.ts
//
// User-facing visibility defaults per item kind. Two layers:
//
//   1. /users/{uid}/preferences/feed_defaults — user's customized
//      defaults. Settings UI (forthcoming) writes here.
//
//   2. HARDCODED_FEED_DEFAULTS — fallback when the user hasn't
//      customized anything. See types.ts for the per-kind reasoning.
//
// `resolveVisibility(uid, kind)` is the single read site. Cached
// per-uid for the page lifetime so the writeFeedItem path doesn't
// hit Firestore on every save.

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import {
  FeedDefaults,
  FeedItemKind,
  FeedVisibility,
  HARDCODED_FEED_DEFAULTS,
} from './types';

const cache = new Map<string, FeedDefaults>();

async function loadFeedDefaults(uid: string): Promise<FeedDefaults> {
  const cached = cache.get(uid);
  if (cached) return cached;

  try {
    const ref = doc(db, 'users', uid, 'preferences', 'feed_defaults');
    const snap = await getDoc(ref);
    if (snap.exists()) {
      // Merge with hardcoded so partial customization is fine.
      const data = snap.data() as Partial<FeedDefaults>;
      const merged: FeedDefaults = { ...HARDCODED_FEED_DEFAULTS, ...data };
      cache.set(uid, merged);
      return merged;
    }
  } catch (err) {
    console.warn('loadFeedDefaults failed, using hardcoded:', err);
  }

  cache.set(uid, HARDCODED_FEED_DEFAULTS);
  return HARDCODED_FEED_DEFAULTS;
}

export async function resolveVisibility(
  uid: string,
  kind: FeedItemKind
): Promise<FeedVisibility> {
  const defaults = await loadFeedDefaults(uid);
  // shared_reaction is a phase-2 internal kind; default to followers.
  if (kind === 'shared_reaction') return 'followers';
  return defaults[kind] ?? HARDCODED_FEED_DEFAULTS[kind] ?? 'private';
}

/** Invalidate cache when the user updates their defaults. */
export function invalidateFeedDefaults(uid: string): void {
  cache.delete(uid);
}
